/**
 * Iter XX.79 — Clarity provider.
 *
 * · Initialises the loader once (idempotent) using
 *   REACT_APP_CLARITY_PROJECT_ID.
 * · Watches route changes via react-router. On sensitive routes
 *   (payments / auth / account) it calls `clarity('stop')`. Off
 *   those routes it calls `clarity('start')`. This is the
 *   compliance-friendly way to keep session-replay off billing and
 *   PII-heavy screens.
 * · Emits `landing_page_viewed` once when the user first lands on
 *   `/` or `/coach-first` (the R3-lite variant).
 * · If the user is signed in, we `identify()` them by user_id so
 *   the recording is attributable in the Clarity dashboard.
 *
 * Mount ONCE at the root under `<BrowserRouter>` — this component
 * renders nothing.
 */
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  init,
  startRecording,
  stopRecording,
  isSensitivePath,
  identify,
  track,
} from "../lib/clarity";
import { applySessionTags, captureUtmsFromUrl, trackOnce } from "../lib/claritySession";
import * as meta from "../lib/metaPixel";
import { useAuth } from "../lib/auth";

export default function ClarityProvider() {
  const location = useLocation();
  const bootedRef = useRef(false);
  const landingFiredRef = useRef(false);
  const identifiedRef = useRef(null);
  const auth = useAuth?.();
  const uid = auth?.user?.user_id || null;

  // ---- One-shot boot ------------------------------------------------
  useEffect(() => {
    if (bootedRef.current) return;
    bootedRef.current = true;
    init();
    // Iter XX.87 — capture UTMs from the entry URL BEFORE any SPA
    // navigation strips them, then set session-level Clarity tags.
    try { captureUtmsFromUrl(); } catch { /* noop */ }
    try { applySessionTags({ authState: uid ? "signed_in" : "anonymous" }); }
    catch { /* noop */ }
    // Iter XX.80 — Boot the Meta Pixel in parallel. Meta's loader
    // fires an initial PageView; subsequent route changes fire
    // additional PageViews below.
    try { meta.init(); } catch { /* noop */ }
  }, [uid]);

  // ---- Re-apply session tags when auth state changes ---------------
  useEffect(() => {
    try { applySessionTags({ authState: uid ? "signed_in" : "anonymous" }); }
    catch { /* noop */ }
  }, [uid]);

  // ---- Identify signed-in users ------------------------------------
  useEffect(() => {
    if (!uid) return;
    if (identifiedRef.current === uid) return;
    identifiedRef.current = uid;
    identify(uid);
    try { meta.setUser({ user_id: uid }); } catch { /* noop */ }
  }, [uid]);

  // ---- Route-aware recording gate ----------------------------------
  useEffect(() => {
    if (isSensitivePath(location.pathname)) {
      stopRecording();
    } else {
      startRecording();
    }
    // Iter XX.80 — Fire a Meta PageView on every SPA route change.
    // Meta's fbq listens to History API natively too, but firing
    // explicitly is deterministic and keeps Test Events populated
    // during QA. Skipped on sensitive routes because we don't want
    // page-level PageView signals from auth/billing screens either.
    if (!isSensitivePath(location.pathname)) {
      try { meta.pageView(); } catch { /* noop */ }
    }
  }, [location.pathname]);

  // ---- landing_page_viewed once per session ------------------------
  useEffect(() => {
    if (landingFiredRef.current) return;
    const p = location.pathname || "";
    if (p === "/" || p === "/coach-first") {
      landingFiredRef.current = true;
      // Iter XX.87 — trackOnce ensures sessionStorage dedup even
      // across full-page reloads within the same tab.
      trackOnce("landing_page_viewed", { path: p });
    }
  }, [location.pathname]);

  return null;
}
